import {
  type InvalidateQueryFilters,
  type Mutation,
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { isMaintenanceFailure } from "./maintenance-retry";
import { reportMutationFailure, reportQueryFailure } from "./handled-failures";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Factory keys to invalidate after the mutation succeeds; the global `MutationCache` handler does it. */
      invalidates?: ReadonlyArray<QueryKey>;
      /** Resolve the mutation only after the invalidated active queries have refetched. */
      awaitInvalidation?: boolean;
      /** Names this mutation in failure reports; defaults to its mutation key or the request route. */
      failureKind?: string;
    };
    queryMeta: {
      /** Names this query in failure reports; defaults to `query:<key segments>`. */
      failureKind?: string;
    };
  }
}

type MutationWithMeta = Pick<Mutation<unknown, unknown, unknown, unknown>, "meta">;

/** The global `MutationCache` `onSuccess` handler: invalidates every key a mutation declares in its `meta`. */
export const invalidateDeclaredQueries = (queryClient: QueryClient, mutation: MutationWithMeta) => {
  const invalidation = Promise.all(
    (mutation.meta?.invalidates ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );

  return mutation.meta?.awaitInvalidation ? invalidation : undefined;
};

/**
 * Invalidates keys after a write that no React Query mutation made, such as an Inertia form submission or visit, or a
 * manual refresh, and after Tree Studio mutations, whose own `QueryClient` has no `MutationCache` handler. Other
 * mutations list their keys in `meta.invalidates` instead.
 */
export const invalidateQueryKeys = (
  queryClient: QueryClient,
  keys: ReadonlyArray<QueryKey>,
  filters?: Omit<InvalidateQueryFilters, "queryKey">,
) => Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ ...filters, queryKey })));

export const createQueryClient = () => {
  const queryClient: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: (count, error) =>
          !isMaintenanceFailure(error) && !(isAxiosError(error) && (error.response?.status ?? 500) < 500) && count < 3,
      },
    },
    // The single place a failed request surfaces: each query error state and failed mutation reports once.
    queryCache: new QueryCache({ onError: reportQueryFailure }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _onMutateResult, mutation) => invalidateDeclaredQueries(queryClient, mutation),
      onError: (error, _variables, _onMutateResult, mutation) => reportMutationFailure(error, mutation),
    }),
  });

  return queryClient;
};
